import SelectInput, {
    SelectOption,
} from '@components/common/SelectInput/SelectInput';
import DatePicker from '@components/common/DatePicker';
import TextInput from '@components/common/TextInput';
import { farmingHist, FarmlandAddReq } from '@/api/growth/farmland/type';
import { useState } from 'react';
import { formatDateString, parseDate } from '@utils/Utils';

interface TillageMngProps {
    formData: FarmlandAddReq;
    onInputChange: (field: string, value: farmingHist[]) => void;
}

const TillageMng = ({ formData, onInputChange }: TillageMngProps) => {
    const [farmingHists, setFarmingHists] = useState<farmingHist[]>(
        formData.farmingHistList?.filter(h => h.category === 'T') || [
            {
                category: 'T',
                workDt: '',
                iamt: '',
                iame: '',
                chcod: '',
                chamt: '',
                chme: '',
                erad: '',
                emax: '',
                emin: '',
                ewind: '',
                ename: '',
                fmcd: '',
                facd: '',
                famn: '',
                famp: '',
                famk: '',
                rcod: '',
                ramt: '',
                rdep: '',
                timpl: '',
                tdep: '',
            },
        ],
    );

    const handleFarmingHistChange = (
        index: number,
        field: keyof farmingHist,
        value: string | number,
    ) => {
        const updated = [...farmingHists];
        updated[index] = { ...updated[index], [field]: value };
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const updateParentFarmingHistList = (tillageData: farmingHist[]) => {
        const otherData =
            formData.farmingHistList?.filter(h => h.category !== 'T') || [];
        const allData = [...otherData, ...tillageData];
        onInputChange('farmingHistList', allData);
    };

    const addFarmingHist = () => {
        const newRecord: farmingHist = {
            category: 'T',
            workDt: '',
            iamt: '',
            iame: '',
            chcod: '',
            chamt: '',
            chme: '',
            erad: '',
            emax: '',
            emin: '',
            ewind: '',
            ename: '',
            fmcd: '',
            facd: '',
            famn: '',
            famp: '',
            famk: '',
            rcod: '',
            ramt: '',
            rdep: '',
            timpl: '',
            tdep: '',
        };
        const updated = [...farmingHists, newRecord];
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const removeFarmingHist = (index: number) => {
        const updated = farmingHists.filter((_, i) => i !== index);
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const tillageImplementOptions: SelectOption[] = [
        { value: 'TI001', label: 'V-형 심토파쇄기 - 단단한 하층토를 파쇄' },
        {
            value: 'TI002',
            label: '심토파쇄기 - 경반층을 깨트려 배수·통기 개선',
        },
        {
            value: 'TI003',
            label: '몰드보드 쟁기 20cm - 흙을 반전시키는 일반 쟁기',
        },
        {
            value: 'TI004',
            label: '치즐 쟁기 (스윕형) - 흙을 뒤집지 않고 부수며 잡초 뿌리 절단',
        },
        {
            value: 'TI005',
            label: '치즐 쟁기 (직선형) - 좁고 뾰족한 날로 깊게 경운',
        },
        {
            value: 'TI006',
            label: '치즐 쟁기 (비틀린 날) - 약간의 흙 뒤집기 효과',
        },
        { value: 'TI007', label: '원반 쟁기 - 거친 땅이나 돌밭 경운용' },
        { value: 'TI008', label: '일방향 원반 쟁기 - 한 방향으로 흙을 밀어냄' },
        { value: 'TI009', label: '탠덤 원반 쟁기 - 2열 배치로 쇄토 효과 큼' },
        {
            value: 'TI010',
            label: '이중 원반 쟁기 - 두 개의 원반이 한 쌍으로 작동',
        },
        {
            value: 'TI011',
            label: '필드 컬티베이터 - 파종 전 표토 정리 및 제초',
        },
        { value: 'TI012', label: '로 컬티베이터 - 작물 줄 사이 제초' },
        { value: 'TI013', label: '휴립 컬티베이터 - 두둑 조성 및 유지 경운' },
        { value: 'TI014', label: '스파이크 써레 - 흙덩이 부수고 평탄화' },
        { value: 'TI015', label: '타인 써레 - 탄력 있는 갈퀴로 표토 정리' },
        { value: 'TI016', label: '리스터 - 깊은 고랑과 두둑을 동시에 조성' },
        { value: 'TI017', label: '베더 - 평평한 침상형 두둑 조성' },
        {
            value: 'TI018',
            label: '블레이드 컬티베이터 - 잡초 뿌리 절단, 토양 교란 최소',
        },
        { value: 'TI019', label: '무수암모니아 시비기 - 가스 비료 토양 주입' },
        { value: 'TI020', label: '액비 주입기 - 액상 가축분뇨 토양 주입' },
        { value: 'TI021', label: '축력 농기구 - 소나 말이 끄는 재래식 농기구' },
        { value: 'TI022', label: '멀치 트레더 - 작물 잔여물 고정 및 혼합' },
        { value: 'TI023', label: '플랭크 - 널빤지로 표토 평탄화' },
        { value: 'TI024', label: '롤러 패커 - 흙덩이 부수고 진압' },
        { value: 'TI025', label: '이중 원반 파종기 - 곡물 파종용 드릴' },
        { value: 'TI026', label: '심구 파종기 - 건조지 수분 확보용 깊은 파종' },
        { value: 'TI031', label: '무경운 파종기 - 경운 없이 직접 파종' },
        {
            value: 'TI032',
            label: '무경운 파종기 (초지용) - 잔디·목초지 직접 파종',
        },
        { value: 'TI033', label: '줄 파종기 - 옥수수·콩 등 정밀 파종' },
        { value: 'TI034', label: '무경운 줄 파종기 - 잔여물 자르며 정밀 파종' },
        { value: 'TI035', label: '파종 막대 - 수동으로 구멍 내어 파종' },
        { value: 'TI036', label: '마트라카 - 손잡이 작동 수동 파종기' },
        { value: 'TI037', label: '로드 위더 - 회전 봉으로 잡초 뿌리 제거' },
        {
            value: 'TI038',
            label: '로터리 호 - 회전 괭이로 어린 잡초 제거 및 표토 쇄토',
        },
        { value: 'TI039', label: '롤러 써레 - 파종 전후 쇄토 및 진압' },
        { value: 'TI041', label: '몰드보드 쟁기 25cm - 흙 반전 쟁기' },
        { value: 'TI042', label: '몰드보드 쟁기 30cm - 심경용 흙 반전 쟁기' },
    ];

    return (
        <div className="tillage-management">
            <div className="row mb-3">
                <div className="col-12 text-end">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addFarmingHist}
                    >
                        <i className="ri-add-line me-1"></i>
                        경운 추가
                    </button>
                </div>
            </div>

            {farmingHists.length > 0 && (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-3">
                        <thead className="text-center">
                            <tr>
                                <th style={{ width: '20%' }}>일자 선택</th>
                                <th style={{ width: '50%' }}>경운기구</th>
                                <th style={{ width: '20%' }}>
                                    경운깊이
                                    <div className="fs-9">(cm)</div>
                                </th>
                                <th style={{ width: '10%' }}>삭제</th>
                            </tr>
                        </thead>
                        <tbody>
                            {farmingHists.map((record, index) => (
                                <tr key={index}>
                                    <td>
                                        <DatePicker
                                            selectedDate={parseDate(
                                                record.workDt || '',
                                            )}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'workDt',
                                                    formatDateString(value),
                                                )
                                            }
                                            size="sm"
                                            placeholder="날짜를 선택해주세요"
                                            maxDate={undefined}
                                            showYearDropdown={false}
                                            showMonthDropdown={false}
                                        />
                                    </td>
                                    <td>
                                        <SelectInput
                                            options={tillageImplementOptions}
                                            value={record.timpl || ''}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'timpl',
                                                    value,
                                                )
                                            }
                                            placeholder="경운기구를 선택해주세요"
                                            selectClassName="form-control"
                                        />
                                    </td>
                                    <td>
                                        <TextInput
                                            value={
                                                record.tdep
                                                    ? record.tdep.toString()
                                                    : ''
                                            }
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'tdep',
                                                    value,
                                                )
                                            }
                                            placeholder="깊이 입력"
                                            type="number"
                                            numericOnly={true}
                                            allowDecimal={true}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={() =>
                                                removeFarmingHist(index)
                                            }
                                            title="삭제"
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {farmingHists.length === 0 && (
                <div className="alert alert-info text-center py-4">
                    <p className="mb-0">
                        <i className="ri-information-line me-2"></i>
                        밭갈이(T) 관련 내용을 입력해주세요. 추가 버튼을 눌러
                        경운 기록을 추가할 수 있습니다.
                    </p>
                </div>
            )}
        </div>
    );
};

export default TillageMng;
